import React, { createContext, useCallback, useContext, useMemo, useState, useEffect, useRef } from 'react';
import { notificationsApi } from '../../services/api';
import { getAccessToken } from '../../lib/supabase';

export type NotificationType = 'info' | 'success' | 'warning' | 'error';

export type Notification = {
  id: string;
  type: NotificationType;
  title?: string;
  message: string;
  duration?: number; // ms
  persistent?: boolean; // whether to save/count as a user notification (badge + history)
  timestamp?: number;
  read?: boolean;
  linkTopicId?: string; // deep-link target parsed from [topic:id] markup
};

type NotificationsContextValue = {
  notify: (n: Omit<Notification, 'id'> | string) => void;
  history: Array<Notification & { timestamp: number; read?: boolean }>;
  unreadCount: number;
  persistentUnreadCount: number;
  markAllRead: () => void;
  markAsRead: (id: string) => void;
  clearAll: () => void;
};

const NotificationsContext = createContext<NotificationsContextValue | undefined>(undefined);

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [items, setItems] = useState<Notification[]>([]);
  const [history, setHistory] = useState<Array<Notification & { timestamp: number; read?: boolean }>>([]);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const eventSourceRef = useRef<{ reader: ReadableStreamDefaultReader; controller: AbortController } | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  const remove = useCallback((id: string) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
  }, []);

  const notify = useCallback((n: Omit<Notification, 'id'> | string) => {
    const obj: Omit<Notification, 'id'> = typeof n === 'string' ? { type: 'info', message: n } : n;

    // Default behavior: `notify()` shows a toast only.
    // Only explicitly-marked notifications should affect the badge / notifications list.
    if (obj.persistent === undefined) obj.persistent = false;
    
    // Check for duplicate messages (same type and message within last 1 second)
    const now = Date.now();
    setItems((prevItems) => {
      const isDuplicate = prevItems.some(item => 
        item.type === obj.type && 
        item.message === obj.message &&
        (now - (item as any).createdAt) < 1000 // Within 1 second
      );
      
      if (isDuplicate) {
        return prevItems; // Don't add duplicate
      }
      
      const id = Math.random().toString(36).slice(2);
      const duration = obj.duration ?? 3500;
      const item: Notification = { id, ...obj, createdAt: now } as Notification & { createdAt: number };
      
      // Add to history (unread) only if persistent
      if (obj.persistent === true) {
        setHistory((prev) => {
          const next = [{ ...item, timestamp: now, read: false }, ...prev].slice(0, 50);
          return next;
        });
      }
      
      if (duration > 0) {
        window.setTimeout(() => remove(id), duration);
      }
      
      return [...prevItems, item];
    });
  }, [remove]);

  // Server-truth unread count to drive badges reliably
  const [serverUnreadCount, setServerUnreadCount] = useState<number>(0);
  const unreadCount = useMemo(() => history.filter(h => !h.read).length, [history]);
  // Prefer server unread count for badges to avoid edge cases; fallback to local when 0
  const persistentUnreadCount = useMemo(() => {
    return serverUnreadCount || history.filter(h => !h.read && h.persistent !== false).length;
  }, [history, serverUnreadCount]);

  const markAllRead = useCallback(async () => {
    try {
      if (isAuthenticated) {
        await notificationsApi.markAllAsRead();
      }
      setHistory((prev) => prev.map(h => ({ ...h, read: true })));
      setServerUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  }, [isAuthenticated]);

  const markAsRead = useCallback(async (id: string) => {
    try {
      if (isAuthenticated) {
        await notificationsApi.markAsRead(id);
      }
      setHistory((prev) => prev.map(h => h.id === id ? { ...h, read: true } : h));
      setServerUnreadCount((c) => Math.max(0, c - 1));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  }, [isAuthenticated]);

  const clearAll = useCallback(() => {
    setHistory([]);
  }, []);


  // Replace [user:id]name[/user] / [topic:id]title[/topic] markup with plain
  // labels for surfaces that render messages as text (bell dropdown, toasts).
  // The profile notifications tab parses the markup into clickable links.
  const stripNotificationMarkup = (message: string) =>
    (message || '').replace(/\[(user|topic):[^\]]+\](.+?)\[\/\1\]/g, '$2');

  // Pull the topic deep-link out of the raw message before markup is stripped
  const extractTopicLinkId = (message: string): string | undefined => {
    const match = /\[topic:([^\]]+)\]/.exec(message || '');
    return match ? match[1] : undefined;
  };

  // Fetch notifications from backend
  const fetchNotifications = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      const data = await notificationsApi.get();
      const backendNotifications = data.notifications.map((n: any) => ({
        id: n.id,
        type: n.type.toLowerCase() as NotificationType,
        title: n.title,
        message: stripNotificationMarkup(n.message),
        linkTopicId: extractTopicLinkId(n.message),
        timestamp: new Date(n.createdAt).getTime(),
        read: n.read,
        persistent: true
      }));
      setHistory(backendNotifications);
      try {
        const { count } = await notificationsApi.getUnreadCount();
        setServerUnreadCount(count || 0);
      } catch {}
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  }, [isAuthenticated]);

  // Set up real-time notifications using custom fetch-based SSE
  const connectToNotificationStream = useCallback(async () => {
    if (!isAuthenticated || eventSourceRef.current) return;
    
    try {
      const token = await getAccessToken();
      if (!token) return;
      
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const url = `${API_BASE_URL}/api/notifications/stream`;
      
      // Use fetch with custom headers instead of EventSource
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'text/event-stream',
          'Cache-Control': 'no-cache'
        }
      });
      
      if (!response.ok) {
        throw new Error(`SSE connection failed: ${response.status}`);
      }
      
      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('Failed to get response reader');
      }
      
      setIsConnected(true);
      
      
      // Store reader reference for cleanup
      eventSourceRef.current = { reader, controller: new AbortController() };
      
      // Read the stream
      const decoder = new TextDecoder();
      let buffer = '';
      
      const readStream = async () => {
        try {
          while (true) {
            const { done, value } = await reader.read();
            
            if (done) {
              break;
            }
            
            buffer += decoder.decode(value, { stream: true });
            
            // Process complete messages
            const lines = buffer.split('\n');
            buffer = lines.pop() || ''; // Keep incomplete line in buffer
            
            for (const line of lines) {
              if (line.startsWith('data: ')) {
                try {
                  const data = JSON.parse(line.slice(6));
                  
                  if (data.type === 'notification') {
                    // Add new notification to history and show toast
                    const newNotification = {
                      ...data.data,
                      message: stripNotificationMarkup(data.data?.message),
                      linkTopicId: extractTopicLinkId(data.data?.message)
                    };
                    setHistory(prev => [{ ...newNotification, persistent: true }, ...prev].slice(0, 50));
                    setServerUnreadCount((c) => c + 1);

                    // Also show as toast
                    const toastNotification: Notification = {
                      id: newNotification.id,
                      type: newNotification.type as NotificationType,
                      title: newNotification.title,
                      message: newNotification.message,
                      linkTopicId: newNotification.linkTopicId,
                      duration: 5000
                    };
                    setItems(prev => [...prev, toastNotification]);

                    // Auto-remove toast after duration
                    setTimeout(() => {
                      setItems(prev => prev.filter(item => item.id !== toastNotification.id));
                    }, 5000);
                  } else if (data.type === 'unread_messages_update') {
                    // Real-time unread message count update
                    window.dispatchEvent(new CustomEvent('unread_messages_update', {
                      detail: { count: data.data.count }
                    }));
                  }
                } catch (error) {
                  console.error('Error parsing SSE message:', error);
                }
              }
            }
          }
        } catch (error) {
          console.error('SSE stream error:', error);
          setIsConnected(false);
        }
      };
      
      readStream();
      
    } catch (error) {
      console.error('Failed to connect to notification stream:', error);
      setIsConnected(false);
    }
  }, [isAuthenticated]);
  
  // Disconnect from notification stream
  const disconnectFromNotificationStream = useCallback(() => {
    if (eventSourceRef.current) {
      try {
        if (eventSourceRef.current.reader) {
          eventSourceRef.current.reader.cancel();
        }
        if (eventSourceRef.current.controller) {
          eventSourceRef.current.controller.abort();
        }
      } catch (error) {
        console.error('Error disconnecting SSE:', error);
      }
      eventSourceRef.current = null;
      setIsConnected(false);
    }
  }, []);

  // Check authentication status and set up real-time notifications
  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Simple check by trying to fetch unread count
        const { count } = await notificationsApi.getUnreadCount();
        setServerUnreadCount(count || 0);
        setIsAuthenticated(true);
      } catch (error) {
        setIsAuthenticated(false);
        disconnectFromNotificationStream();
      }
    };
    
    checkAuth();
  }, [disconnectFromNotificationStream]);

  // Poll server unread count periodically as a fallback to keep badges in sync
  useEffect(() => {
    if (!isAuthenticated) return;
    const t = setInterval(async () => {
      try {
        const { count } = await notificationsApi.getUnreadCount();
        setServerUnreadCount(count || 0);
      } catch {}
    }, 30000);
    return () => clearInterval(t);
  }, [isAuthenticated]);
  
  // Set up real-time connection when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchNotifications(); // Initial fetch
      connectToNotificationStream(); // Connect to real-time stream
    } else {
      disconnectFromNotificationStream();
    }
    
    return () => {
      disconnectFromNotificationStream();
    };
  }, [isAuthenticated, connectToNotificationStream, disconnectFromNotificationStream, fetchNotifications]);

  const value = useMemo(() => ({ 
    notify, 
    history, 
    unreadCount,
    persistentUnreadCount,
    markAllRead, 
    markAsRead,
    clearAll, 
    fetchNotifications,
    isConnected 
  }), [notify, history, unreadCount, persistentUnreadCount, markAllRead, markAsRead, clearAll, fetchNotifications, isConnected]);

  // Dev-friendly hooks: expose a global and a custom event for testing
  React.useEffect(() => {
    try {
      // Expose history for fallback rendering
      (window as any).__notifications_history = history;
      // Quick global helper for console testing
      (window as any).notify = notify;
      // Custom event: window.dispatchEvent(new CustomEvent('app:notify', { detail }))
      const onEvent = (e: Event) => {
        const detail = (e as CustomEvent).detail;
        if (!detail) return;
        if (typeof detail === 'string') notify(detail);
        else notify(detail);
      };
      window.addEventListener('app:notify', onEvent as EventListener);
      const onMarkAllRead = () => { try { markAllRead(); } catch {} };
      window.addEventListener('notifications:markAllRead', onMarkAllRead as EventListener);
      return () => {
        window.removeEventListener('app:notify', onEvent as EventListener);
        window.removeEventListener('notifications:markAllRead', onMarkAllRead as EventListener);
      };
    } catch {}
  }, [notify, markAllRead, history]);

  return (
    <NotificationsContext.Provider value={value}>
      {children}
      {/* Toast container */}
      <div className="toast-container" aria-live="polite" aria-atomic="true">
        {items.map((n) => (
          <div
            key={n.id}
            className={`toast toast-${n.type}`}
            role="status"
            style={n.linkTopicId ? { cursor: 'pointer' } : undefined}
            title={n.linkTopicId ? 'Open topic' : undefined}
            onClick={() => {
              if (!n.linkTopicId) return;
              remove(n.id);
              window.location.href = `/t/${encodeURIComponent(n.linkTopicId)}`;
            }}
          >
            <div className="toast-content">
              {n.title && <div className="toast-title">{n.title}</div>}
              <div className="toast-message">{n.message}</div>
            </div>
            <button className="toast-close" onClick={(e) => { e.stopPropagation(); remove(n.id); }} aria-label="Close">×</button>
          </div>
        ))}
      </div>

      {/* No modal here; notifications live under the Profile modal */}
    </NotificationsContext.Provider>
  );
};

export function useNotifications(): NotificationsContextValue & { 
  fetchNotifications: () => Promise<void>;
  isConnected: boolean;
} {
  const ctx = useContext(NotificationsContext);
  if (!ctx) throw new Error('useNotifications must be used within NotificationProvider');
  return ctx as NotificationsContextValue & { 
    fetchNotifications: () => Promise<void>;
    isConnected: boolean;
  };
}
